import React, { useMemo, useCallback, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import { BaseForm, BaseFormFieldConfig, BaseFormSectionConfig } from '../shared/FormComponents';
import { FormSchema, FormField } from '../../utils/validation/form-schema';
import DetailFrame from '../shared/Frames/DetailFrame';
import {
  createReview,
  extractPlaybook,
  REVIEW_EXTRACT_STEP,
  REVIEW_EXTRACT_COMPLETED,
  REVIEW_EXTRACT_FAILED,
  REVIEW_EXTRACT_CONNECTING,
  REVIEW_EXTRACT_CONNECTED,
  REVIEW_EXTRACT_STEPS,
  REVIEW_EXTRACT_RESET,
  REVIEW_EXTRACTING_PLAYBOOK,
} from '../../store/actions/Review/ReviewActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import SSECreationProgress, { IndexStep, UiStepStatus } from '../shared/SSECreationProgress';
import { ExtractionStep } from '../../types/review';
import { getMultiHubSocketService } from '../../utils/MultiHubSocketService';
import { fetchLinkableProjects } from '../../store/actions/Projects/ProjectActions';
import { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, createPlaybookFormTourSteps } from '../shared/FeatureTour';

// Step name mapping from AI Engine step names to frontend step IDs
const STEP_NAME_TO_ID: Record<string, string> = {
  'Downloading playbook': 'download',
  'Analyzing content': 'analyze',
  'Extracting rules': 'extract',
  'Saving to playbook': 'save',
};

const CreatePlaybookForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [projectOptions, setProjectOptions] = useState<DropdownOption[]>([]);
  const pubsubCleanupRef = useRef<(() => void) | null>(null);

  // Fetch linkable projects on mount
  React.useEffect(() => {
    const loadProjects = async () => {
      try {
        const projectsResp = await dispatch(fetchLinkableProjects() as any);
        if (projectsResp && Array.isArray(projectsResp)) {
          setProjectOptions(projectsResp.map((p: any) => ({ value: p.uuid, label: p.title })));
        }
      } catch (error: any) {
        console.error('[CreatePlaybookForm] Error loading projects:', error?.message);
      }
    };
    loadProjects();
  }, [dispatch]);

  // Read creation progress state from review-specific extraction state
  const { isConnecting, connected, steps, complete, ok, summary } = useSelector(
    (state: RootState) => state.review.extractionProgress,
  );

  // ─── Tour ──────────────────────────────────────────────────────────
  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'create-playbook-form',
    steps: createPlaybookFormTourSteps,
    readyToShow: !isCreating && !isSubmitting && !isConnecting,
    autoStartDelay: 600,
  });

  // Map ExtractionStep[] → IndexStep[] for SSECreationProgress
  const STATUS_MAP: Record<string, UiStepStatus> = {
    pending: 'Pending',
    in_progress: 'In Progress',
    completed: 'Completed',
    failed: 'Failed',
  };

  const indexSteps: IndexStep[] = useMemo(
    () =>
      steps.map((s: ExtractionStep) => ({
        id: s.id,
        label: s.name,
        status: STATUS_MAP[s.status] || 'Pending',
        messages: s.message ? [s.message] : [],
      })),
    [steps],
  );

  const breadcrumbs = useMemo(
    () => [
      { name: t('Templates'), href: '/templates?type=playbooks', current: false },
      { name: t('New Playbook'), href: '/templates/add?type=playbooks', current: true },
    ],
    [t],
  );

  const schema = useMemo(
    () =>
      new FormSchema('createPlaybook', [
        new FormField('name', 'text', false, t('Playbook Name')),
        new FormField('document', 'text', true, t('Playbook Document'), {
          customValidator: (value: any) => {
            if (!value || (Array.isArray(value) && value.length === 0)) {
              return { isValid: false, error: t('Upload a playbook document to extract rules from') };
            }
            return { isValid: true };
          },
        }),
      ]),
    [t],
  );

  const sections: BaseFormSectionConfig[] = useMemo(
    () => [
      {
        dataTourId: 'playbook-form-name',
        fields: [
          {
            name: 'name',
            type: 'input',
            label: t('Playbook Name'),
            placeholder: t('Optional — AI will extract the name from the document'),
            required: false,
            gridSize: 'full',
          } as BaseFormFieldConfig,
        ],
      },
      {
        dataTourId: 'playbook-form-upload',
        title: t('Upload Playbook Document'),
        description: t(
          "Upload your firm's playbook or negotiation guidelines. AI will extract clause rules, global rules, agreement types, and other metadata automatically.",
        ),
        fields: [
          {
            name: 'document',
            type: 'fileUploader',
            label: t('Document'),
            required: true,
            gridSize: 'full',
            maxFiles: 1,
            allowedExtensions: ['pdf', 'docx', 'doc', 'txt'],
          } as BaseFormFieldConfig,
        ],
      },
      {
        dataTourId: 'playbook-form-collaborators',
        title: t('Access'),
        description: t('Optionally invite team members who should have access to this playbook.'),
        fields: [
          {
            name: 'collaborators',
            type: 'collaboratorSelector' as any,
            label: t('Access'),
            required: false,
            gridSize: 'full',
            customProps: {
              allowedPermissions: ['edit', 'view'],
              showOwnerSection: false,
            },
          } as BaseFormFieldConfig,
        ],
      },
      ...(projectOptions.length > 0 ? [{
        title: t('Link to Project'),
        description: t('Optionally link this playbook to an existing project.'),
        fields: [
          {
            name: 'project_uuid',
            type: 'dropdown' as const,
            label: t('Project'),
            placeholder: t('Select a project'),
            required: false,
            gridSize: 'full' as const,
            customProps: {fullWidth: true} as any,
            options: projectOptions,
          } as BaseFormFieldConfig,
        ],
      }] : []),
    ],
    [t, projectOptions],
  );

  // ── PubSub connection for live progress ──────────────────────────
  const connectToPubSub = useCallback(
    async (correlationId: string, reviewUuid: string) => {
      const hub = 'ai_processing';
      const group = `playbook_${reviewUuid}`;
      const socketService = getMultiHubSocketService();

      try {
        const isConnected = await socketService.connectToHub(hub);
        if (!isConnected) {
          console.warn('[CreatePlaybookForm] Failed to connect to PubSub hub');
          return;
        }

        // Join the review processing group
        socketService.emit(hub, 'join', { group });

        // Listen for all events
        const handleEvent = (eventData: any) => {
          // Only handle events for our correlation ID
          if (
            eventData?.correlationId !== correlationId &&
            eventData?.correlation_id !== correlationId
          ) {
            return;
          }

          const subType = eventData?.data?.sub_type || eventData?.sub_type || '';
          const stepName = eventData?.data?.step_name || eventData?.stepName || '';
          const message = eventData?.message || '';
          const stepId = STEP_NAME_TO_ID[stepName] || stepName;

          if (subType === 'step_started') {
            dispatch({
              type: REVIEW_EXTRACT_STEP,
              payload: { id: stepId, status: 'in_progress', message, name: stepName },
            });
          } else if (subType === 'step_progress') {
            dispatch({
              type: REVIEW_EXTRACT_STEP,
              payload: { id: stepId, status: 'in_progress', message, name: stepName },
            });
          } else if (subType === 'step_completed') {
            dispatch({
              type: REVIEW_EXTRACT_STEP,
              payload: { id: stepId, status: 'completed', message, name: stepName },
            });
          } else if (subType === 'step_failed') {
            dispatch({
              type: REVIEW_EXTRACT_STEP,
              payload: { id: stepId, status: 'failed', message, name: stepName },
            });
          } else if (subType === 'completed') {
            // Full pipeline complete — clean up PubSub, reset state
            if (pubsubCleanupRef.current) {
              pubsubCleanupRef.current();
              pubsubCleanupRef.current = null;
            }

            dispatch({ type: REVIEW_EXTRACTING_PLAYBOOK, payload: false });
            dispatch({ type: REVIEW_EXTRACT_RESET });
            setIsCreating(false);
            setIsSubmitting(false);

            // dispatch(
            //   showFeedbackModal({
            //     showModal: true,
            //     message: t('Playbook created successfully!'),
            //     modalType: 'success',
            //     duration: 3000,
            //   }),
            // );
            history.push(`/playbooks/${reviewUuid}`);
          } else if (subType === 'failed' || subType === 'step_failed') {
            const error = eventData?.data?.error || message || 'Processing failed';

            if (pubsubCleanupRef.current) {
              pubsubCleanupRef.current();
              pubsubCleanupRef.current = null;
            }

            dispatch({ type: REVIEW_EXTRACTING_PLAYBOOK, payload: false });
            dispatch({ type: REVIEW_EXTRACT_FAILED, payload: { error } });
            dispatch(
              showFeedbackModal({
                showModal: true,
                message: error,
                modalType: 'error',
                duration: 4000,
              }),
            );
            setIsCreating(false);
            setIsSubmitting(false);
          }
        };

        socketService.on(hub, '*', handleEvent);

        // Store cleanup function
        pubsubCleanupRef.current = () => {
          socketService.off(hub, '*', handleEvent);
          socketService.emit(hub, 'leave', { group });
        };
      } catch (err) {
        console.error('[CreatePlaybookForm] PubSub connection error:', err);
      }
    },
    [dispatch, history, t],
  );

  const handleSubmit = useCallback(
    async (formData: Record<string, any>) => {
      setIsSubmitting(true);
      setIsCreating(true);

      // Initialize Redux state for SSECreationProgress
      dispatch({ type: REVIEW_EXTRACT_RESET });
      dispatch({ type: REVIEW_EXTRACT_CONNECTING });

      try {
        // Step 1: Create the review (playbook) with a name or placeholder
        const name = formData.name?.trim() || 'Untitled Playbook';
        const collaborators = (formData.collaborators || []).map((c: any) => ({
          collaborator_uuid: c.collaborator_uuid || c.user_id,
          access: c.access || 'view',
        }));
        const createPayload: any = { name, collaborators };
        if (formData.project_uuid) {
          createPayload.project_uuid = formData.project_uuid;
        }
        const result = await dispatch(createReview(createPayload) as any);

        if (!result?.uuid) {
          setIsSubmitting(false);
          setIsCreating(false);
          dispatch({ type: REVIEW_EXTRACT_RESET });
          return;
        }

        // Step 2: Extract playbook rules from the uploaded document
        const uploadedFiles = formData.document;
        let file: File | null = null;
        if (Array.isArray(uploadedFiles) && uploadedFiles.length > 0) {
          file = uploadedFiles[0];
        } else if (uploadedFiles instanceof File) {
          file = uploadedFiles;
        }

        if (file) {
          // Mark connected and initialize steps
          dispatch({ type: REVIEW_EXTRACT_CONNECTED });
          dispatch({
            type: REVIEW_EXTRACT_STEPS,
            payload: {
              steps: [
                { id: 'download', name: 'Downloading playbook', status: 'pending' },
                { id: 'analyze', name: 'Analyzing content', status: 'pending' },
                { id: 'extract', name: 'Extracting rules', status: 'pending' },
                { id: 'save', name: 'Saving to playbook', status: 'pending' },
              ],
            },
          });

          const extractResult = await dispatch(extractPlaybook(result.uuid, file) as any);

          // Connect to PubSub for live progress
          if (extractResult?.correlation_id) {
            await connectToPubSub(extractResult.correlation_id, result.uuid);
          } else {
            // No PubSub correlation — navigate immediately
            dispatch({ type: REVIEW_EXTRACT_COMPLETED, payload: { ok: true } });
            setIsCreating(false);
            setIsSubmitting(false);
            history.replace(`/playbooks/${result.uuid}`);
          }
          return;
        }

        // No file — just navigate to the playbook detail
        setIsCreating(false);
        setIsSubmitting(false);
        dispatch({ type: REVIEW_EXTRACT_RESET });
        history.replace(`/playbooks/${result.uuid}`);
      } catch (err) {
        setIsSubmitting(false);
        setIsCreating(false);
        dispatch({ type: REVIEW_EXTRACT_RESET });
      }
    },
    [dispatch, history, connectToPubSub],
  );

  const handleCancel = useCallback(() => {
    history.push('/templates?type=playbooks');
  }, [history]);

  // ── Progress view (shown while AI is extracting) ─────────────────
  if (isCreating || isSubmitting || isConnecting) {
    const handleCancelCreation = () => {
      if (pubsubCleanupRef.current) {
        pubsubCleanupRef.current();
        pubsubCleanupRef.current = null;
      }
      dispatch({ type: REVIEW_EXTRACT_RESET });
      setIsCreating(false);
      setIsSubmitting(false);
      history.push('/templates?type=playbooks');
    };

    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <SSECreationProgress
          title={t('Extracting Playbook')}
          isConnecting={isConnecting}
          steps={indexSteps}
          connected={connected}
          complete={complete}
          ok={ok}
          summary={summary ?? undefined}
          onCancel={handleCancelCreation}
          type="playbook"
        />
      </div>
    );
  }

  return (
    <DetailFrame breadcrumbs={breadcrumbs} actions={[]}>
      <BaseForm
        title={t('Create Playbook')}
        description={t(
          "Upload your firm's playbook document and AI will extract all clause rules, global rules, and metadata automatically.",
        )}
        sections={sections}
        schema={schema}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        submitLabel={isSubmitting ? t('Creating...') : t('Create & Extract')}
        cancelLabel={t('Cancel')}
      />
      <FeatureTour
        steps={createPlaybookFormTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </DetailFrame>
  );
};

export default CreatePlaybookForm;
